/**
 * "Sheet" — a mesma base `Dialog` de `ModalCadastro.tsx`, mas o conteúdo desliza da
 * borda direita e ocupa a altura toda. É o padrão de **criação** do cadastro: um
 * painel lateral com os campos mínimos, sem tirar a lista de baixo dos olhos.
 *
 * A animação de slide vem do `tw-animate-css` (importado em `index.css`) via os
 * `data-[state=open|closed]` que o Radix aplica sozinho. O menu de país do
 * `CampoTelefone` usa `z-tooltip`, acima do `z-modal` daqui — então abre por cima.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type * as React from 'react';
import { cn } from '@/lib/utils';

export function Sheet({
  aberto,
  onAbrir,
  titulo,
  descricao,
  children,
  rodape,
}: {
  aberto: boolean;
  onAbrir: (v: boolean) => void;
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
  rodape?: React.ReactNode;
}) {
  return (
    <Dialog.Root open={aberto} onOpenChange={onAbrir}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed inset-y-0 right-0 z-[var(--z-modal)] flex w-[min(30rem,calc(100vw-2rem))] flex-col',
            'border-l border-border bg-surface shadow-[var(--shadow-lg)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            'duration-[var(--dur-base)]',
          )}
        >
          <div className="flex items-start justify-between border-b border-border p-6">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="tipo-subtitulo text-text">{titulo}</Dialog.Title>
              {descricao && (
                <Dialog.Description className="text-[13px] leading-[1.5] text-text-secondary">
                  {descricao}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Fechar"
              className="rounded-md p-2 text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
            >
              <X className="size-5" />
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-6">{children}</div>

          {rodape && (
            <div className="flex justify-end gap-3 border-t border-border p-6">{rodape}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
